import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, CalendarDays, Clock, FileText, ExternalLink, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import PublicPage from "./PublicPage";

export function PromotionsPage() {
  return <PublicPage slug="promotions" />;
}

export function PromotionDetailPage() {
  const { id } = useParams();
  const [promo, setPromo] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPromo(null);
    setNotFound(false);
    api.get(`/public/promotions/${id}`)
      .then(({ data }) => {
        setPromo(data);
        document.title = `${data.title} | LD Travel Promotions`;
        window.scrollTo(0, 0);
      })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center section-pad">
        <div className="text-center px-4">
          <h1 className="font-heading text-4xl text-[#0B1D3A] font-semibold">Promotion not available</h1>
          <p className="text-slate-500 mt-3">This offer may have expired or been removed.</p>
          <Link to="/promotions" className="btn-brand mt-6 inline-flex" data-testid="promo-notfound-back">View Current Promotions</Link>
        </div>
      </main>
    );
  }

  if (!promo) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-amber-400/30 border-t-amber-600 rounded-full animate-spin" aria-label="Loading" />
      </main>
    );
  }

  const images = promo.images || [];

  return (
    <main className="bg-[#FBF9F5]" data-testid="promotion-detail-page">
      <section className="bg-[#061325] text-amber-50 py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <Link to="/promotions" data-testid="promo-back-link" className="inline-flex items-center gap-2 text-amber-200/80 text-sm hover:text-amber-200 mb-6">
            <ArrowLeft className="w-4 h-4" aria-hidden="true" /> All promotions
          </Link>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="font-heading text-4xl sm:text-5xl font-medium" data-testid="promo-detail-title">
            {promo.title}
          </motion.h1>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-5 text-sm text-slate-300">
            {promo.destination && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-amber-400" aria-hidden="true" /> {promo.destination}</span>}
            {promo.travel_dates && <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-amber-400" aria-hidden="true" /> {promo.travel_dates}</span>}
            {promo.booking_deadline && <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-amber-400" aria-hidden="true" /> Book by {promo.booking_deadline}</span>}
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        {images.length > 0 && (
          <div className={`grid gap-4 ${images.length > 1 ? "sm:grid-cols-2" : ""}`}>
            {images.map((img, i) => (
              <img key={i} src={img.url} alt={img.alt || promo.title} loading={i === 0 ? "eager" : "lazy"}
                data-testid={`promo-detail-image-${i}`}
                className={`rounded-2xl object-cover w-full ${i === 0 && images.length > 1 ? "sm:col-span-2 h-72 sm:h-[420px]" : "h-56 sm:h-64"}`}
                style={{ objectPosition: `${img.focal?.x ?? 50}% ${img.focal?.y ?? 50}%` }} />
            ))}
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8 mt-10">
          <div className="lg:col-span-2">
            {promo.description && (
              <>
                <h2 className="font-heading text-2xl sm:text-3xl text-[#0B1D3A] font-semibold mb-4">About This Offer</h2>
                {promo.description.split("\n").filter(Boolean).map((p, i) => (
                  <p key={i} className="text-slate-600 leading-relaxed mb-4">{p}</p>
                ))}
              </>
            )}
            {promo.terms && (
              <div className="mt-8 card-luxe p-6">
                <h3 className="flex items-center gap-2 font-semibold text-[#0B1D3A] mb-2">
                  <FileText className="w-4 h-4 text-amber-600" aria-hidden="true" /> Terms & Restrictions
                </h3>
                <p className="text-xs text-slate-500 leading-relaxed whitespace-pre-line" data-testid="promo-terms">{promo.terms}</p>
              </div>
            )}
          </div>
          <aside className="lg:sticky lg:top-24 self-start">
            <div className="card-luxe p-7 border-t-4 !border-t-[#D4AF37]">
              {promo.price && (
                <p className="mb-5">
                  <span className="text-xs uppercase tracking-widest text-slate-500 block">{promo.price_label || "Starting at"}</span>
                  <span className="font-heading text-4xl font-semibold text-amber-700">{promo.price}</span>
                </p>
              )}
              <a href={promo.button_url} target="_blank" rel="noopener noreferrer" data-testid="promo-detail-book-button" className="btn-brand w-full justify-center">
                {promo.button_text || "View Offer / Book"} <ExternalLink className="w-4 h-4" aria-hidden="true" />
                <span className="sr-only">(opens an external website in a new tab)</span>
              </a>
              <p className="text-xs text-slate-400 mt-4 text-center">You'll be taken to Lisa's travel website to view this offer and continue booking.</p>
              <Link to="/contact" data-testid="promo-detail-question-link" className="block text-center text-sm text-amber-700 hover:text-amber-800 font-medium mt-4">
                Have a question? Contact Lisa
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}
